import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoadDataFacade, SetInitialStateFacade } from '../../../../../common/models';
import { ListSearchCriteriaFacade, ListSearchCriteriaType } from '../../../list-search-criteria';
import { ShipmentPlannerProfileItemState } from './shipment-planner-profile-item.state';
import {
  ShipmentPlannerProfileItemCreate,
  ShipmentPlannerProfileItemInitialState,
  ShipmentPlannerProfileItemLoad,
  ShipmentPlannerProfileItemUpdate,
  ShipmentPlannerDefaultProfileItemLoad,
  ShipmentPlannerDefaultProfileItemSetToggle,
  ShipmentPlannerProfileItemReset,
} from './shipment-planner-profile-item.actions';
import { ShipmentPlannerProfileModel } from '../models';
import { MatDialogRef } from '@angular/material/dialog';
import { ShipmentSearchCriteria } from '../../shipment';

@Injectable({ providedIn: 'root' })
export class ShipmentPlannerProfileItemFacade implements LoadDataFacade, SetInitialStateFacade {
  profileId$: Observable<number> = this.store.select(ShipmentPlannerProfileItemState.getProfileId);
  profile$: Observable<ShipmentPlannerProfileModel> = this.store.select(ShipmentPlannerProfileItemState.getProfile);
  isDefaultProfileApplicable$: Observable<boolean> = this.store
    .select(ShipmentPlannerProfileItemState.getProfileId)
    .pipe(
      switchMap((profileId: number) =>
        this.listSearchCriteriaFacade
          .getSearchCriteriaByListType(ListSearchCriteriaType.Shipment)
          .pipe(
            map(
              ({ filters, shipmentStatisticFilter, dndStatisticFilter }: ShipmentSearchCriteria) =>
                !profileId && !this.areAnyFiltersApplied({ filters, shipmentStatisticFilter, dndStatisticFilter }),
            ),
          ),
      ),
    );
  loaded$: Observable<boolean> = this.store.select(ShipmentPlannerProfileItemState.getLoaded);

  constructor(private store: Store, private listSearchCriteriaFacade: ListSearchCriteriaFacade) {}

  loadDefaultProfile(): void {
    this.store.dispatch(new ShipmentPlannerDefaultProfileItemLoad());
  }

  setDefaultProfileToggle(profileId: number, isDefault: boolean): void {
    this.store.dispatch(new ShipmentPlannerDefaultProfileItemSetToggle({ profileId, isDefault }));
  }

  loadAll(shipmentPlannerProfileId: number): void {
    this.store.dispatch(new ShipmentPlannerProfileItemLoad(shipmentPlannerProfileId));
  }

  createProfile(profile: Partial<ShipmentPlannerProfileModel>, dialog: MatDialogRef<unknown>): void {
    this.store.dispatch(new ShipmentPlannerProfileItemCreate({ dialog, profile }));
  }

  updateProfile(data: Partial<ShipmentPlannerProfileModel>): void {
    this.store.dispatch(new ShipmentPlannerProfileItemUpdate(data));
  }

  clearProfile(): void {
    this.store.dispatch(new ShipmentPlannerProfileItemReset());
  }

  setInitialState(): void {
    this.store.dispatch(new ShipmentPlannerProfileItemInitialState());
  }

  private areAnyFiltersApplied(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value !== 'object' || value instanceof Date) {
      return true;
    }

    if (Array.isArray(value)) {
      return value.some((item: unknown) => this.areAnyFiltersApplied(item));
    }

    if (typeof value === 'object') {
      return Object.keys(value).some(
        (key: string) => value.hasOwnProperty(key) && this.areAnyFiltersApplied(value[key]),
      );
    }
  }
}
